import config  from '../../../config';

// This is a utility file which uses either a versioned storage or a optional passed key to update storage on a user browser.
// Configuration: the application config is currently looking for a "config.app.storageVersion" located in the config file. example : { app: { storageVersion: 'v1' }}
// The typical usage of this would be to allow for being able to wipe user preferences in the event of a large change that would need for users to reinstate certain variables that have changed.
// However, you can pass a optional true to the functions to allow for bypassing all versioning.

/**
 * @description Sets a specific key and value in the users browser storage.
 * @param {*} key name representing the value
 * @param {*} value object
 * @param {boolean} [noVersion=false] optional skip to versioning of storage
 * @param {Storage} [system=localStorage] storage provider | default localStorage | options: localStorage, sessionStorage
 * @return {void} no return
 */
export const setStorageItem = (key: string, value: any, noVersion = false, noSolution = false, system = localStorage) => {
    if (key !== null && key !== undefined) {
        const json = JSON.stringify(value);
        
        let trueKey = `${key}`;
        if (!noVersion) {
            trueKey = `${trueKey}-${config.app.storageVersion}`;
        }

        if (!noSolution) {
            trueKey = `${config.app.solutionKey}-${trueKey}`
        }

        system.setItem(trueKey, json);
    }
};

/**
 * @description Gets a specific value from the users browser storage.
 * @param {*} key name representing the value
 * @param {boolean} [noVersion=false] optional skip to versioning of storage
 * @param {Storage} [system=localStorage] storage provider | default localStorage | options: localStorage, sessionStorage
 * @return {*} object | value
 */
export const getStorageItem = (key: string, noVersion = false, noSolution = false, system = localStorage) => {
    if (key !== null && key !== undefined) {
        let trueKey = `${key}`;
        if (!noVersion) {
            trueKey = `${trueKey}-${config.app.storageVersion}`;
        }

        if (!noSolution) {
            trueKey = `${config.app.solutionKey}-${trueKey}`
        }

        const json = system.getItem(trueKey);
        if (json) {
            const jsonSanitized = typeof (json) === 'string' && json.trim().length > 0 ? json.trim() : '';
            if (jsonSanitized && jsonSanitized.length > 0) {
                return JSON.parse(jsonSanitized);
            } else {
                return null;
            }
        }
        return null;
    }
    return null;
};

/**
 * @description Removes the specific passed key from the users browser storage.
 * @param {*} key name representing the value
 * @param {boolean} [noVersion=false] optional skip to versioning of storage
 * @param {Storage} [system=localStorage] storage provider | default localStorage | options: localStorage, sessionStorage
 * @return {void} no return
 */
export const removeStorageItem = (key: string, noVersion = false, noSolution = false, system = localStorage) => {
    if (key !== null && key !== undefined) {

        let trueKey = `${key}`;
        if (!noVersion) {
            trueKey = `${trueKey}-${config.app.storageVersion}`;
        }

        if (!noSolution) {
            trueKey = `${config.app.solutionKey}-${trueKey}`
        }

        system.removeItem(trueKey);
    }
};

/**
 * @description Clears the entire storage regardless of other keys present (might have unintended consequences if used).
 * @param {Storage} [system=localStorage] storage provider | default localStorage | options: localStorage, sessionStorage
 * @param {string[]} [keysToAvoid=[]] keys to ignore during deletion process
 * @return {void} no return
 */
export const clearAllStorageItems = (system = localStorage, keysToAvoid: any = []) => {
    if (system && system.length > 0 && keysToAvoid && keysToAvoid.length > 0) {
        const keysToDelete: string[] = [];
        keysToAvoid = Array.from(keysToAvoid).map(m => `${config.app.solutionKey}-${m}-${config.app.storageVersion}`);
        Object.entries(system).forEach(f => {
            if (!keysToAvoid.includes(f[0])) {
                keysToDelete.push(f[0]);
            }
        });
        keysToDelete.forEach((f) => removeStorageItem(f, true, true, system));
    } else if (system && system.length > 0) {
        system.clear();
    } else {
        console.log('Storage system is unavailable.', 'error');
    }
};

export const getSessionStorageItem = (key: string) =>  getStorageItem(key, undefined, undefined, sessionStorage)
export const setSessionStorageItem = (key: string, value: any) =>  setStorageItem(key, value, undefined, undefined, sessionStorage)
export const removeSessionStorageItem = (key: string) =>  removeStorageItem(key, undefined, undefined, sessionStorage)

const applicationStorage = {
    setStorageItem,
    getStorageItem,
    removeStorageItem,
    clearAllStorageItems,
    getSessionStorageItem,
    setSessionStorageItem,
    removeSessionStorageItem
};

export default applicationStorage;